import React, { useEffect, useMemo, useCallback } from "react";
import { Card, Table, Typography, Tag, Spin, Alert, Row, Col, Select, Space } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { useInjectReducer, useInjectSaga } from "redux-injectors";

import { reducer, sliceKey, actions } from "./slice";
import { commoditySamplingDetailedSaga, DETAILED_SAGA_KEY } from "./saga";
import {
  selectCommodityTableData,
  selectCommodityTotal,
  selectCommodityLoading,
  selectCommodityError,
  selectCommodityPagination,
} from "./selectors";

const { Title, Text } = Typography;
const { Option } = Select;

const CommoditySamplingDetailed = ({ filters = {} }) => {
  const history = useHistory();
  const dispatch = useDispatch();
 
  useInjectReducer({ key: sliceKey, reducer });
  useInjectSaga({
    key: DETAILED_SAGA_KEY,
    saga: commoditySamplingDetailedSaga,
  });

  const data = useSelector(selectCommodityTableData);
  const total = useSelector(selectCommodityTotal);
  const loading = useSelector(selectCommodityLoading);
  const error = useSelector(selectCommodityError);
  const pagination = useSelector(selectCommodityPagination);

  // Memoize filters to prevent infinite renders
  const memoizedFilters = useMemo(() => ({
    companyIds: filters.companyIds || [],
    projectCodeIds: filters.projectCodeIds || [],
    selectedState: filters.selectedState || [],
    sortDescending: filters.sortDescending ?? true
  }), [
    filters.companyIds,
    filters.projectCodeIds,
    filters.selectedState,
    filters.sortDescending
  ]);

  const filtersString = useMemo(() => {
    return JSON.stringify({
      selectedClients: memoizedFilters.companyIds,
      selectedProjects: memoizedFilters.projectCodeIds,
      ...memoizedFilters,
    });
  }, [memoizedFilters]);

  // Create stable callback for navigation
  const handleBackNavigation = useCallback(() => {
    history.push("/bo/dashboard", { filters: memoizedFilters });
  }, [history, memoizedFilters]);

  // Handle pagination changes
  const handleTableChange = useCallback((page, pageSize) => {
    dispatch(actions.setPagination({ currentPage: page, pageSize }));
  }, [dispatch]);

  const handlePageSizeChange = useCallback((newPageSize) => {
    dispatch(actions.setPageSize(newPageSize));
  }, [dispatch]);

  // Reset pagination when filters change
  useEffect(() => {
    dispatch(actions.resetPagination());
  }, [dispatch, filtersString]);

  // Fetch data effect
  useEffect(() => {
    dispatch(
      actions.fetchDetailed({
        filtersString,
        page: pagination.currentPage,
        limit: pagination.pageSize,
      })
    );
  }, [dispatch, filtersString, pagination.currentPage, pagination.pageSize]);

  // Memoized table data with unique keys
  const tableData = useMemo(() => {
    if (!Array.isArray(data)) return [];
    
    return data.map((item, idx) => ({
      ...item,
      key: `${item.commodityName}-${idx}`,
      id: `${item.commodityName}-${idx}`,
      serialNumber: idx + 1 + (pagination.currentPage - 1) * pagination.pageSize,
    }));
  }, [data, pagination.currentPage, pagination.pageSize]);

  // Pagination configuration for Ant Design Table
  const paginationConfig = {
    current: pagination.currentPage,
    pageSize: pagination.pageSize,
    total: total,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => 
      `${range[0]}-${range[1]} of ${total} entries`,
    pageSizeOptions: ['10', '30', '50', '100'],
    onChange: handleTableChange,
    onShowSizeChange: handleTableChange,
    style: { marginTop: 16 }
  };

  const columns = [
    { 
      title: "#", 
      dataIndex: "serialNumber", 
      key: "serialNumber", 
      width: 50 
    },
    {
      title: "Commodity Name",
      dataIndex: "commodityName",
      key: "commodityName",
      width: 180,
    },
    {
      title: "Total Orders",
      dataIndex: "totalOrders",
      key: "totalOrders",
      width: 100,
    },
    {
      title: "Collection Initiated",
      dataIndex: "collectionInitiated",
      key: "collectionInitiated",
      width: 100,
      align:"center"
    },
    {
      title: "Samples Collected",
      dataIndex: "samplesCollected",
      key: "samplesCollected",
      render: (val) => <Tag color="green">{val}</Tag>,
      width: 130,
    },
    {
      title: "Under Correction",
      dataIndex: "needCorrection",
      key: "needCorrection",
      render: (val) => <Tag color="orange">{val}</Tag>,
      width: 130,
    },
    {
      title: "Regret Orders",
      dataIndex: "regret",
      key: "regret",
      render: (val) => <Tag color="red">{val}</Tag>,
      width: 100,
    },
    {
      title: "Not Found Cases",
      dataIndex: "notFound",
      key: "notFound",
      render: (val) => <Tag>{val}</Tag>,
      width: 120,
    },
    {
      title: "Pending Orders",
      dataIndex: "pending",
      key: "pending",
      //render: (val) => <Tag>{val}</Tag>,
      width: 120,
    },
    {
      title: "Sampling Completion%",
      dataIndex: "samplingCompletionScore",
      key: "samplingCompletionScore",
      //render: (val) => <Tag color="black">{val}%</Tag>,
      width: 120,
    },
    {
      title: "Completion%",
      dataIndex: "completionScore",
      key: "completionScore",
      render: (val) => <Tag color="black">{val}%</Tag>,
      width: 120,
    },
  ];

  return (
    <Card
      style={{ marginTop: 24 }}
      bodyStyle={{ padding: 0, backgroundColor: "#0D9488" }} // Teal-green background
    >
      <Row align="middle" style={{ padding: "16px 24px" }}>
        <Col>
          <ArrowLeftOutlined
            style={{
              fontSize: 18,
              color: "#fff",
              marginRight: 12,
              cursor: "pointer",
            }}
            onClick={handleBackNavigation}
          />
        </Col>
        <Col flex="auto">
          <Title level={4} style={{ color: "#fff", margin: 0 }}>
            Complete Commodity Sampling Analytics
          </Title>
        </Col>
        <Col>
          <Space align="center">
            <Text style={{ color: "#fff", fontSize: 14 }}>Show:</Text>
            <Select
              value={pagination.pageSize}
              onChange={handlePageSizeChange}
              style={{ width: 80 }}
              size="small"
            >
              <Option value={10}>10</Option>
              <Option value={30}>30</Option>
              <Option value={50}>50</Option>
              <Option value={100}>100</Option>
            </Select>
            <Text style={{ color: "#fff", fontSize: 14 }}>entries</Text>
          </Space>
        </Col>
      </Row>

      {/* Main Content */}
      <div style={{ padding: 24, backgroundColor: "#fff" }}>
        {error && (
          <Alert
            message="Error loading data"
            description={error}
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        {/* Summary Info */}
        {!loading && tableData.length > 0 && (
          <div style={{ marginBottom: 16, color: "#666" }}>
            <Text>
              Showing {Math.min(pagination.pageSize, tableData.length)} of {total} commodities
              {memoizedFilters.companyIds?.length > 0 && 
                ` (filtered by ${memoizedFilters.companyIds.length} client${memoizedFilters.companyIds.length > 1 ? 's' : ''})`
              }
            </Text>
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: "center", padding: "50px" }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>
              Loading commodity sampling analytics data...
            </div>
          </div>
        ) : (
          <div>
            <Title level={5}>
              Complete Commodity Sampling Analytics ({total} Commodities)
            </Title>
            <Table
              columns={columns}
              dataSource={tableData}
              pagination={paginationConfig}
              rowKey="id"
              scroll={{ x: true }}
              bordered
            />
          </div>
        )}
      </div>
    </Card>
  );
};

export default CommoditySamplingDetailed;